import { Suspense, useState, useEffect } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { AssignmentSheetContent } from "@/app/ProDrivers/WorkOrders/Components/Sheets/AssignmentSheetContent";
import { AssignmentDriverDetailsTab } from "@/app/ProDrivers/WorkOrders/Components/Sheets/AssignmentDriverDetailsTab";
import { AssignmentFilesTab } from "@/app/ProDrivers/WorkOrders/Components/Sheets/AssignmentFilesTab";
import { Badge } from "@/components/catalyst/badge";
import { Avatar } from "@/components/catalyst/avatar";
import { TaxRegistration } from "@/gql/graphql";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { AssignmentStatusBadge } from "@/app/ProDrivers/WorkOrders/Components/AssignmentStatusBadge";
import { workOrderAssignmentRecordQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderAssignmentQuery";
import { getInitials } from "@/lib/Utils/getInitials";
import {
  DocumentTextIcon,
  PaperClipIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { assignmentRecordRoute } from "@/app/ProDrivers/Common/Routes/proDriverRoutes";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import { useWorkOrderAssignmentActions } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderAssignmentActions";
import { useUpdateAssignmentStatus } from "@/app/ProDrivers/WorkOrders/Hooks/useUpdateAssignmentStatus";

type TabId = "assignment" | "driver" | "files";

export function AssignmentDrawerRoute() {
  const navigate = useNavigate();
  const { assignmentId } = useParams({ from: assignmentRecordRoute.id });
  const [activeTab, setActiveTab] = useState<TabId>("assignment");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  function handleClose() {
    setIsOpen(false);
    setTimeout(() => {
      navigate({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/assignments",
        params: (prev) => prev,
        search: (prev) => prev,
      });
    }, 300);
  }

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <DrawerOverlay />
      <DrawerContent showCloseButton={true} onClose={handleClose}>
        <div className="flex h-full flex-col overflow-hidden">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <AssignmentSheetHeader
              assignmentId={assignmentId}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            <DrawerBody>
              <TabContent activeTab={activeTab} assignmentId={assignmentId} />
            </DrawerBody>
          </Suspense>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function TabContent({
  activeTab,
  assignmentId,
}: {
  activeTab: TabId;
  assignmentId: string;
}) {
  switch (activeTab) {
    case "assignment":
      return <AssignmentSheetContent assignmentId={assignmentId} />;
    case "driver":
      return <AssignmentDriverDetailsTab assignmentId={assignmentId} />;
    case "files":
      return <AssignmentFilesTab assignmentId={assignmentId} />;
    default:
      return null;
  }
}

function AssignmentSheetHeader({
  assignmentId,
  activeTab,
  setActiveTab,
}: {
  assignmentId: string;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const { data } = useSuspenseGqlQuery(workOrderAssignmentRecordQuery, {
    id: assignmentId,
  });
  const getActions = useWorkOrderAssignmentActions();

  const assignment = data.workOrderAssignment;

  const { handleStatusChange, isPending } = useUpdateAssignmentStatus(
    assignment?.id ?? "",
  );

  if (!assignment) {
    return null;
  }

  return (
    <DrawerHeader>
      <div className="flex items-start gap-4 px-6 py-5">
        <Avatar
          initials={getInitials(assignment.proDriver.name!)}
          className="ring-border h-12 w-12 shrink-0 bg-white shadow-sm ring-1"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <DrawerTitle>{assignment.proDriver.name}</DrawerTitle>
            <AssignmentStatusBadge
              status={assignment.status}
              onStatusChange={handleStatusChange}
              disabled={isPending}
            />
            {assignment.proDriver.tax_registration !== TaxRegistration.None ? (
              <Badge color="blue">
                {assignment.proDriver.tax_registration.replace("_", " ")}
              </Badge>
            ) : null}
          </div>
          <DrawerDescription>
            {assignment.proDriver.email}
            {assignment.proDriver.phone
              ? ` · ${assignment.proDriver.phone}`
              : null}
          </DrawerDescription>
        </div>
        <IconMenuActions actions={getActions(assignment)} />
      </div>
      <div className="px-6">
        <AssignmentTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </DrawerHeader>
  );
}

function AssignmentTabs({
  activeTab,
  setActiveTab,
}: {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const tabs = [
    {
      id: "assignment" as const,
      name: "Assignment",
      Icon: DocumentTextIcon,
    },
    {
      id: "driver" as const,
      name: "Driver",
      Icon: UserIcon,
    },
    {
      id: "files" as const,
      name: "Files",
      Icon: PaperClipIcon,
    },
  ];

  return (
    <div className="flex gap-1">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`group relative flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-all duration-200 ease-in-out ${
            activeTab === tab.id
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <tab.Icon
            className={`h-5 w-5 flex-shrink-0 transition-colors duration-200 ${
              activeTab === tab.id
                ? "text-indigo-600"
                : "text-gray-400 group-hover:text-gray-500"
            }`}
          />
          <span>{tab.name}</span>
        </button>
      ))}
    </div>
  );
}
